import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/portal/ApiConfig";
import {Mail, PortalApi, WebmailApi} from "components/src/api/portal/generated";
import {AccessibleSpinner} from "components/src/common/AccessibleSpinner";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {logger} from "components/src/utils/Logger";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import {useEffect, useState} from "react";
import {Trash as DeleteIcon} from 'react-bootstrap-icons';
import Button from 'react-bootstrap/Button';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import ListGroup from 'react-bootstrap/ListGroup';
import Modal from 'react-bootstrap/Modal';
import Spinner from "react-bootstrap/Spinner";
import {useTranslation} from "react-i18next";

/*
 * REST api for accessing the back-end
 */
const webmailApi = new WebmailApi(ApiConfig.config());
const portalApi = new PortalApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface ReplyProps {
    sourceMail : Mail
    sourceBody : Array<string> | undefined
    onClose : OnClose
}

export const ReplyPopup = ({sourceMail, sourceBody, onClose} : ReplyProps) =>
{
    /**
     * Body of the reply
     */
    const [body, setBody] = useState<string>(getInitialBody());
    /**
     * Attachments
     */
    const [attachments, setAttachments] = useState<File[]>([]);

    /*
     * Max size in bytes of all attachments
     */
    const [attachmentsMaxSize, setAttachmentsMaxSize] = useState<number | undefined>();
    /*
     * Max size in bytes of the reply body
     */
    const [bodyMaxSize, setBodyMaxSize] = useState<number | undefined>();
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {spinnerStateCounter, setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * Set to true while sending to show a spinner on the download button
     */
    const [sending, setSending] = useState<boolean>(false);
    /**
     * i18next translation hook
     */
    const {t} = useTranslation('components');

    function getInitialBody()
    {
        let initialBody : string = "";

        if (sourceBody != undefined) {
            // and newline and prepend each line with >
            initialBody = "\n" + sourceBody.join("\n").split('\n').map(s => `> ${s}`).join('\n');
        }

        return initialBody;
    }

    useEffect(() =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        portalApi.portalControllerGetPortalProperties().then(
            (response) => {
                setAttachmentsMaxSize(response.data.replyAttachmentsMaxSize)
                setBodyMaxSize(response.data.replyBodyMaxSize)
            }
        )
        .finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [setSpinnerStateCounter]);

    const sendReply = () =>
    {
        // reset error message
        setErrorMessage(undefined);

        setSending(true);

        webmailApi.webmailPortalControllerReply(sourceMail.folder, sourceMail.uid, body, attachments).then(() => {
            logger.debug("Reply sent")
            onClose(DialogCloseCause.Succes);
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error sending reply", err);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            setSending(false);
        });
    }

    function addAttachments(fileList : FileList){
        setAttachments(prevAttachments => [...prevAttachments, ...Array.from(fileList)]);
    }

    function removeAttachment(file : File){
        setAttachments(prevAttachments => prevAttachments.filter(f => f !== file));
    }

    // called when the dialog is closed without sending the reply
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    return (
        <div>
            <Modal show={true} onHide={() => onHideDialog()} size="xl" fullscreen="lg-down" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t('webmail-reply.title')} <AccessibleSpinner show={spinnerStateCounter > 0} />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row flex-grow-1">
                        <main className="col d-flex flex-column overflow-auto modal-main-body">
                            <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                         tooltipPlacement={"bottom"}/>

                            <Form>
                                <FloatingLabel label={t('webmail-reply.subject-label')} className="text-secondary mb-3">
                                    <Form.Control type="text" value={t('webmail-reply.subject-prefix') + sourceMail.subject} placeholder="Subject"
                                                  disabled={true}/>
                                </FloatingLabel>

                                <FloatingLabel label={t('webmail-reply.to-label')} className="text-secondary mb-3">
                                    <Form.Control type="text" value={sourceMail.from} placeholder="To"
                                                  disabled={true}/>
                                </FloatingLabel>

                                <Form.Group className="mb-3">
                                    <Form.Label>{t('webmail-reply.attachment-label')}</Form.Label>&nbsp;{attachmentsMaxSize !== undefined &&
                                            <span className="text-secondary">(max. {Utils.formatBytes(attachmentsMaxSize)})</span>}
                                    <Form.Control
                                        size="sm"
                                        type="file"
                                        multiple={true}
                                        accept=""
                                        onChange={e => addAttachments(((e.target as HTMLInputElement).files as FileList))}
                                    />
                                </Form.Group>

                                <ListGroup>
                                    {attachments?.map(file => (
                                        <ListGroup.Item key={file.name} className="border-0">
                                            <DeleteIcon title={t('webmail-reply.remove-attachment-title')} onClick={() => {removeAttachment(file)}}/> {file.name}
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>

                                <Form.Group className="mb-3">
                                    <Form.Label className="text-secondary">{t('webmail-reply.body-label')}</Form.Label>&nbsp;{bodyMaxSize !== undefined &&
                                            <span className="text-secondary">(max. {Utils.formatBytes(bodyMaxSize, 0)})</span>}
                                    <Form.Control
                                        className="font-monospace white-space-pre"
                                        as="textarea"
                                        onChange={e => {setBody(e.target.value)}}
                                        value={body}
                                        rows={8}
                                        autoFocus
                                    />
                                </Form.Group>
                            </Form>
                        </main>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="success"
                            onClick={sendReply}
                            disabled={StringUtils.isBlank(body)}>
                        {sending && <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            aria-hidden="true"
                            className="me-1"
                        />}
                        {sending ? t('webmail-reply.sending') : t('webmail-reply.send')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}